import { HighlightedLineManager } from './highlightedLineManager';
import { WallManager } from './wallManager';
import { WallExtremePointManager } from './wallExtremePointManager';
import { globalStores } from '@/editor/stores';
import { TextInfoManager } from './textInfoManager';
import { CeilManager } from './ceilManager';
import { EEntityType, ESceneMode } from '@/editor/types';
import { WindowManager } from './windowManager';
import { SelectedHoveredManager } from './selectedHoveredManager';
import { StructManager } from './structManager';
import { TransformManager } from './transformManager';
import { FloorPlanManager } from './floorPlanManager';
import { FurnitureManager } from './furnitureManager';
import { SeparateWallManager } from './separateWallManager';
import { RegionManager } from './regionManager';
import { FaceManager } from './faceManager';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { IndicatorManager } from './indicatorManager';
import { RoomHighlightManager } from './roomHighlightManager';
import { WallOutlineManager } from './wallOutlineManager';
import { RoomManager } from './roomManager';
import { ModelGroupManager } from './modelGroupManager';
import { AssemblyManager } from './assemblyManager';
import { WallRulerManager } from './rulerManager';
import { AlignManager } from './alignManager';
import { ArrayPreviewManager } from './arrayPreviewManager';
import type WallEntity from '@/editor/entities/wallEntity';
import type SeparateWallEntity from '@/editor/entities/separateWallEntity';
import type RoomEntity from '@/editor/entities/roomEntity';
import type WindowEntity from '@/editor/entities/windowEntity';
import type { ColumnEntity } from '@/editor/entities/columnEntity';
import type FurnitureEntity from '@/editor/entities/furnitureEntity';
import type RegionEntity from '@/editor/entities/regionEntity';
import type FaceEntity from '@/editor/entities/faceEntity';
import type ModelGroupEntity from '@/editor/entities/modelGroupEntity';
import type AssemblyEntity from '@/editor/entities/assemblyEntity';
import type { WallExtremePointEntity } from '@/editor/entities/wallExtremePointEntity';
import type TransformEntity from '@/editor/entities/transformEntity';
import { DimensionManager } from './dimensionManager';
import type { AreaDimensionEntity } from '@/editor/entities/areaDimensionEntity';
import AreaDimensionTransformEntity from '@/editor/entities/areaDimensionTransformEntity';
import { LinearDimensionEntity } from '@/editor/entities/linearDimensionEntity';
import { isMultiSelectDoorWindowOnly, isMultiSelectFurnitureOrAssemblyOnly } from '@/editor/utils/select/multi-select';

interface IValidEntityTypeEntityMap {
  [EEntityType.Wall]: WallEntity;
  [EEntityType.Room]: RoomEntity;
  [EEntityType.Region]: RegionEntity;
  [EEntityType.DoorWindow]: WindowEntity;
  [EEntityType.Column]: ColumnEntity;
  [EEntityType.WallExtremePoint]: WallExtremePointEntity;
  [EEntityType.Transform]: TransformEntity;
  [EEntityType.Furniture]: FurnitureEntity;
  [EEntityType.ModelGroup]: ModelGroupEntity;
  [EEntityType.Assembly]: AssemblyEntity;
  [EEntityType.SeparateWall]: SeparateWallEntity;
  [EEntityType.Face]: FaceEntity;
  [EEntityType.AreaDimension]: AreaDimensionEntity;
  [EEntityType.AreaTransform]: AreaDimensionTransformEntity;
  [EEntityType.LinearDimension]: LinearDimensionEntity;
}

type TEntityTypeEntityMap = IValidEntityTypeEntityMap & {
  [K in Exclude<EEntityType, keyof IValidEntityTypeEntityMap>]: undefined;
};

export class ModelManager {
  private _wallManager: WallManager = new WallManager();
  private _regionManager: RegionManager = new RegionManager();
  private _roomManager: RoomManager = new RoomManager();
  private _highlightedLineManager: HighlightedLineManager = new HighlightedLineManager();
  private _wallExtremePointManager: WallExtremePointManager = new WallExtremePointManager();
  private _textInfoManager: TextInfoManager = new TextInfoManager();
  private _ceilManager: CeilManager = new CeilManager();
  private _floorPlanManager: FloorPlanManager = new FloorPlanManager();
  private _structManager: StructManager = new StructManager();
  private _separateWallManager: SeparateWallManager = new SeparateWallManager();
  private _indicatorManager: IndicatorManager = new IndicatorManager();
  private _arrayPreviewManager: ArrayPreviewManager = new ArrayPreviewManager();

  windowManager: WindowManager = new WindowManager();
  transformManager: TransformManager = new TransformManager();
  furnitureManager: FurnitureManager = new FurnitureManager();
  faceManager: FaceManager = new FaceManager();
  selectedHoveredManager: SelectedHoveredManager = new SelectedHoveredManager();
  roomHighlightManager: RoomHighlightManager = new RoomHighlightManager();
  wallOutlineManager = new WallOutlineManager();
  modelGroupManager: ModelGroupManager = new ModelGroupManager();
  assemblyManager: AssemblyManager = new AssemblyManager();
  alignManager: AlignManager = new AlignManager();
  wallRulerManager: WallRulerManager = new WallRulerManager();
  dimensionManager: DimensionManager = new DimensionManager();

  constructor() {
    editorEvent.on(EditorEventName.SCENE_MODE_CHANGE, this.forceUpdateEntity);
  }

  get wallManager() {
    return this._wallManager;
  }

  get regionManager() {
    return this._regionManager;
  }

  get roomManager() {
    return this._roomManager;
  }

  get highlightedLineManager() {
    return this._highlightedLineManager;
  }

  get wallExtremePointManager() {
    return this._wallExtremePointManager;
  }

  get textInfoManager() {
    return this._textInfoManager;
  }

  get ceilManager() {
    return this._ceilManager;
  }

  get separateWallManager() {
    return this._separateWallManager;
  }

  get structManager() {
    return this._structManager;
  }

  get floorPlanManager() {
    return this._floorPlanManager;
  }

  get indicatorManager() {
    return this._indicatorManager;
  }

  get arrayPreviewManager() {
    return this._arrayPreviewManager;
  }

  updateEntity() {
    const roomDatas = globalStores.roomStore.getRoomDatas();

    this._wallManager.update();
    // this._floorManager.update(roomDatas);

    this._regionManager.update(roomDatas);
    this._roomManager.update(roomDatas);
    this.wallOutlineManager.update();

    this._wallExtremePointManager.update();
    this._textInfoManager.update();
    this._ceilManager.update();
    this._structManager.update();
    this._separateWallManager.update();

    this.windowManager.update();
    this.furnitureManager.update();
    this.assemblyManager.update();

    this.faceManager.update();
    this.dimensionManager.update();
    this.selectedRerender();

    this.wallRulerManager.update();
    this.hoverRerender();
  }

  // 强制更新所有实体，在2d/3d场景模式切换时调用
  private forceUpdateEntity = () => {
    this.ceilManager.forceUpdate();
    this.furnitureManager.forceUpdate();
    this.windowManager.forceUpdate();
    this.regionManager.forceUpdate();
    this.roomManager.forceUpdate();
    this.faceManager.forceUpdate();
    this.selectedRerender();
  };

  /**
   * 根据 entityId 和 type 获取实体
   * @returns entity
   */
  public getEntity<T extends EEntityType>(entityId: string, type: T): TEntityTypeEntityMap[T] | undefined {
    if (!entityId || !type) return;

    const entityFetchers: { [key in EEntityType]?: (id: string) => TEntityTypeEntityMap[key] | undefined } = {
      [EEntityType.Wall]: (id) => this._wallManager.getWallEntity(id),
      [EEntityType.Region]: (id) => this._regionManager.getEntityById(id),
      [EEntityType.Room]: (id) => this._roomManager.getEntityById(id),
      [EEntityType.DoorWindow]: (id) => this.windowManager.getDoorWindowEntity(id),
      [EEntityType.Column]: (id) => this._structManager.getColumnEntity(id),
      [EEntityType.WallExtremePoint]: (id) => this._wallExtremePointManager.getEntity(id),
      [EEntityType.Transform]: () => this.transformManager.getTransformEntity(),
      [EEntityType.AreaTransform]: () => this.transformManager.getAreaTransformEntity(),
      [EEntityType.Furniture]: (id) => this.furnitureManager.getEntity(id),
      [EEntityType.ModelGroup]: (id) => this.modelGroupManager.getEntity(id),
      [EEntityType.Assembly]: (id) => this.assemblyManager.getEntity(id),
      [EEntityType.SeparateWall]: (id) => this.separateWallManager.getEntity(id),
      [EEntityType.Face]: (id) => this.faceManager.getEntity(id),
      [EEntityType.AreaDimension]: (id) => this.dimensionManager.getAreaDimension(id),
      [EEntityType.LinearDimension]: (id) => this.dimensionManager.getLinearDimension(id)
    };
    return entityFetchers[type]?.(entityId);
  }

  private _checkSelectedShouldCleared() {
    const { selected } = globalStores.editorStateStore;
    const data = globalStores.getDataByIdType(selected.entityId, selected.type);
    if (!data) {
      globalStores.editorStateStore.clearSelected();
      return;
    }
    // 若切换2/3D模式时当前多选选中隔墙，则清空选中
    if (
      globalStores.sceneMode === ESceneMode.scene3d &&
      selected.type === EEntityType.MultiModel &&
      'modelInfos' in data &&
      !(isMultiSelectFurnitureOrAssemblyOnly(data) || isMultiSelectDoorWindowOnly(data))
    ) {
      globalStores.editorStateStore.clearSelected();
      return;
    }
  }

  selectedRerender() {
    this._checkSelectedShouldCleared();

    this.selectedHoveredManager.updateSelected();
    this.transformManager.refreshBySelectedEntity();
  }

  hoverRerender() {
    const hoverEntity = globalStores.editorStateStore.hoverEntity;
    if (hoverEntity.entityId && !this.getEntity(hoverEntity.entityId, hoverEntity.type)) {
      globalStores.editorStateStore.clearHoverEntity();
    }
    this.selectedHoveredManager.updateHover();
  }

  dispose() {
    editorEvent.off(EditorEventName.SCENE_MODE_CHANGE, this.forceUpdateEntity);
    this.dimensionManager.dispose();
  }
}

export const modelManager = new ModelManager();
